import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCurrentUser } from '../services/authService';
import { getEmployerApplications } from '../services/applicationService';
import { isPermissionDenied, fetchPublicWorkerName } from '../services/publicWorkerProfileService';
import { useWindowSize, BP } from '../hooks/useWindowSize';

// Same status palette ViewApplicantsPage.js uses (getStatusColor), kept as a
// small local copy — this app's convention (see ManageJobsPage.js's own
// STATUS_STYLES/STATUS_LABELS) is per-page status maps rather than a shared
// file, since label sets differ slightly between jobs and applications.
const STATUS_META = {
  pending: { label: 'Pending', color: '#f59e0b' },
  reviewing: { label: 'Reviewing', color: '#3b82f6' },
  send_offer: { label: 'Send Offer', color: '#7c3aed' },
  offer_sent: { label: 'Offer Sent', color: '#0f766e' },
  hired: { label: 'Hired', color: '#10b981' },
  rejected: { label: 'Rejected', color: '#ef4444' },
};

function StatusBadge({ status, rejectedReason }) {
  const meta = STATUS_META[status] || { label: status, color: '#6b7280' };
  const label = status === 'rejected' && rejectedReason === 'job_canceled' ? 'Rejected (Job Canceled)' : meta.label;
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}`, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// Shared by AllApplicantsPage and AllHiredPage — a filtered view over the same
// cross-job applications stream rather than a separate page/component, since
// "All Hired" is just "All Applicants" with status === 'hired'.
function ApplicantsListPage({ statusFilter, title, emptyMessage }) {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const user = getCurrentUser();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDetailsMap, setUserDetailsMap] = useState({});

  useEffect(() => {
    if (!user) return;
    const unsub = getEmployerApplications(user.uid, (apps) => {
      setApplications(apps);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  // Fetch worker display details (name/email/photo) for any applicant userId
  // not already cached — mirrors ViewApplicantsPage.js's fetchUserDetails,
  // but cached across the whole cross-job list instead of per single-job load.
  //
  // HC-INFRA-008C, Prompt #422 — reads via the relationship-verified
  // getWorkerContactDetails callable (Prompt #415's shared
  // verifyEmployerWorkerRelationship gate), never a direct users/{uid}
  // read — this page renders email directly per applicant. Already
  // parallelized across every missing applicant via the outer Promise.all;
  // a per-applicant denial/failure (relationship no longer qualifies, or
  // any other error) is caught individually so one bad lookup can't stop
  // the rest of the batch from resolving, falling back to the same
  // "Unknown User" placeholder this page already used for a missing doc.
  useEffect(() => {
    const missing = [...new Set(applications.map(a => a.userId))].filter(uid => uid && !userDetailsMap[uid]);
    if (missing.length === 0) return;
    let cancelled = false;
    const functions = getFunctions();
    const getWorkerContactDetailsFn = httpsCallable(functions, 'getWorkerContactDetails');
    Promise.all(missing.map(async (uid) => {
      try {
        const result = await getWorkerContactDetailsFn({ workerId: uid });
        const data = result.data || {};
        return [uid, {
          name: data.displayName || data.email?.split('@')[0] || 'Unknown User',
          email: data.email || 'No email',
          photoURL: data.photoURL || null,
        }];
      } catch (error) {
        // Bug A (T19, Prompt #520): on permission-denied, the name (only)
        // comes from the public projection; email stays denied. Never users/{uid}.
        const name = isPermissionDenied(error) ? await fetchPublicWorkerName(uid) : null;
        return [uid, { name: name || 'Unknown User', email: 'No email', photoURL: null }];
      }
    })).then((entries) => {
      if (cancelled) return;
      setUserDetailsMap(prev => {
        const next = { ...prev };
        entries.forEach(([uid, details]) => { next[uid] = details; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [applications, userDetailsMap]);

  const displayedApplications = useMemo(() => {
    const list = statusFilter ? applications.filter(a => a.status === statusFilter) : applications;
    return [...list].sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bTime - aTime;
    });
  }, [applications, statusFilter]);

  const emptyState = (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
      <p style={{ fontSize: '16px', marginBottom: 0 }}>{emptyMessage}</p>
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: '#0F1E2E' }}>{title}</h2>
        <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '13px' }}>
          {displayedApplications.length} {displayedApplications.length === 1 ? 'applicant' : 'applicants'}
        </p>
      </div>

      {isMobile ? (
        /* ── MOBILE: card layout ── */
        <div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading applicants...</div>
          ) : displayedApplications.length === 0 ? (
            <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>{emptyState}</div>
          ) : (
            displayedApplications.map((applicant) => {
              const details = userDetailsMap[applicant.userId];
              return (
                <div
                  key={applicant.id}
                  onClick={() => navigate(`/jobs/${applicant.jobId}/applicants`)}
                  style={{ backgroundColor: '#F8FAFC', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '12px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      {details?.photoURL ? (
                        <img src={details.photoURL} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '16px', flexShrink: 0 }}>
                          {(details?.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {details?.name || 'Loading…'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {details?.email || ''}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={applicant.status} rejectedReason={applicant.rejectedReason} />
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                    {[applicant.jobTitle, applicant.company].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Applied {formatDate(applicant.createdAt)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* ── TABLET / DESKTOP: CSS Grid table ── */
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <div style={{ minWidth: '780px' }}>
            <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading applicants...</div>
              ) : displayedApplications.length === 0 ? (
                emptyState
              ) : (
                <div style={{ width: '100%' }}>
                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '0.75rem 1rem', gap: '0.5rem' }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Applicant</div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Job</div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Applied</div>
                  </div>

                  {/* Data rows */}
                  {displayedApplications.map((applicant) => {
                    const details = userDetailsMap[applicant.userId];
                    return (
                      <div
                        key={applicant.id}
                        onClick={() => navigate(`/jobs/${applicant.jobId}/applicants`)}
                        style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', borderBottom: '1px solid #e5e7eb', padding: '1rem', gap: '0.5rem', alignItems: 'center', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                          {details?.photoURL ? (
                            <img src={details.photoURL} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                              {(details?.name || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F1E2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {details?.name || 'Loading…'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {details?.email || ''}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {applicant.jobTitle || 'Job'}
                          {applicant.company && (
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{applicant.company}</div>
                          )}
                        </div>
                        <div>
                          <StatusBadge status={applicant.status} rejectedReason={applicant.rejectedReason} />
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {formatDate(applicant.createdAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AllApplicantsPage() {
  return (
    <ApplicantsListPage
      statusFilter={null}
      title="All Applicants"
      emptyMessage="No applicants yet. Applications will appear here as workers apply to your jobs."
    />
  );
}

export function AllHiredPage() {
  return (
    <ApplicantsListPage
      statusFilter="hired"
      title="All Hired"
      emptyMessage="No hires yet. Workers you've hired across all your jobs will appear here."
    />
  );
}

export default AllApplicantsPage;
